import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, Typography, message, Space, InputRef, theme } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { CitadeloLogo } from '../../components/CitadeloLogo';
import { useTheme } from '../../hooks/useTheme';

const { Title, Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
}

interface MFAFormData {
  token: string;
}

export const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [mfaForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const { login, verifyMFA } = useAuth();
  const navigate = useNavigate();
  const mfaInputRef = useRef<InputRef>(null);
  const { token } = theme.useToken();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (showMFA && mfaInputRef.current) {
        // Slight delay to ensure modal/transition animation is complete
        setTimeout(() => {
            mfaInputRef.current?.focus();
        }, 100);
    }
  }, [showMFA]);

  const onFinish = async (values: LoginFormData) => {
    setLoading(true);
    try {
      const result = await login(values.username, values.password);
      
      if (result.success) {
        if (result.requiresMFA) {
          setShowMFA(true);
          message.info('Please enter your MFA code');
        } else if (result.needsMfaSetup) {
          message.info('Please set up MFA to continue');
          navigate('/mfa-setup');
        } else {
          message.success('Login successful!');
          navigate('/projects');
        }
      } else {
        message.error(result.error || 'Login failed');
      }
    } catch (error) {
      message.error('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const onMFAFinish = async (values: MFAFormData) => {
    setMfaLoading(true);
    try {
      const result = await verifyMFA(values.token);
      
      if (result.success) {
        message.success('MFA verification successful!');
        navigate('/projects');
      } else {
        message.error(result.error || 'MFA verification failed');
      }
    } catch (error) {
      message.error('An error occurred during MFA verification');
    } finally {
      setMfaLoading(false);
    }
  };

  if (showMFA) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)'
      }}>
        <Card style={{ 
          width: 400, 
          boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(26, 26, 46, 0.2)',
          borderRadius: '16px',
          border: isDarkMode ? '1px solid #424242' : '1px solid rgba(255, 255, 255, 0.1)',
          background: token.colorBgContainer
        }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #ff6b35, #ff8c42)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <SafetyCertificateOutlined style={{ fontSize: '32px', color: '#ffffff' }} />
              </div>
              <div>
                <Title level={3} style={{ color: token.colorTextHeading, marginBottom: '8px' }}>
                  Multi-Factor Authentication
                </Title>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Please enter the 6-digit code from your authenticator app
                </Text>
              </div>
            </Space>
          </div>

          <Form
            form={mfaForm}
            layout="vertical"
            onFinish={onMFAFinish}
          >
            <Form.Item
              name="token"
              label={<span style={{ color: token.colorText, fontWeight: '500' }}>Authentication Code</span>}
              rules={[
                { required: true, message: 'Please enter your authentication code' },
                { len: 6, message: 'Code must be 6 digits' },
                { pattern: /^\d{6}$/, message: 'Code must contain only numbers' }
              ]}
            >
              <Input
                ref={mfaInputRef}
                placeholder="123456"
                maxLength={6}
                style={{ 
                  textAlign: 'center', 
                  fontSize: '18px', 
                  letterSpacing: '2px',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={mfaLoading} 
                size="large"
                block
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontWeight: '500'
                }}
              >
                Verify Code
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Button 
              type="link" 
              onClick={() => setShowMFA(false)}
              style={{ color: '#ff6b35', fontWeight: '500' }}
            >
              Back to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)'
    }}>
      <Card style={{ 
        width: 400, 
        boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(26, 26, 46, 0.2)',
        borderRadius: '16px',
        border: isDarkMode ? '1px solid #424242' : '1px solid rgba(255, 255, 255, 0.1)',
        background: token.colorBgContainer
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <CitadeloLogo size="medium" variant="full" theme="light" />
            <div>
              <Title level={3} style={{ color: token.colorTextHeading, marginBottom: '8px' }}>
                Client Portal
              </Title>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Sign in to access your projects
              </Text>
            </div>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="username"
            label={<span style={{ color: token.colorText, fontWeight: '500' }}>Username or Email</span>}
            rules={[{ required: true, message: 'Please enter your username or email' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#ff6b35' }} />}
              placeholder="Enter your username or email"
              size="large"
              style={{
                borderRadius: '8px',
                padding: '12px'
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={<span style={{ color: token.colorText, fontWeight: '500' }}>Password</span>}
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#ff6b35' }} />}
              placeholder="Enter your password"
              size="large"
              style={{
                borderRadius: '8px',
                padding: '12px'
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              size="large"
              block
              style={{
                height: '48px',
                borderRadius: '8px',
                fontWeight: '500'
              }}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
